import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import NewsCard from "@/components/news/NewsCard";
import { GridSkeleton } from "@/components/news/Skeletons";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";
import { TID } from "@/lib/testIds";

export default function SearchResultsPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const q = params.get("q") || "";
  const [term, setTerm] = useState(q);
  const [data, setData] = useState({ items: [], loading: false, total: 0 });

  useEffect(() => {
    setTerm(q);
    if (!q.trim()) {
      setData({ items: [], loading: false, total: 0 });
      return;
    }
    setData({ items: [], loading: true, total: 0 });
    api
      .search(q, 60)
      .then((r) => setData({ items: r.items || [], loading: false, total: r.total || 0 }))
      .catch(() => setData({ items: [], loading: false, total: 0 }));
  }, [q]);

  const submit = (e) => {
    e.preventDefault();
    if (!term.trim()) return;
    navigate(`/buscar?q=${encodeURIComponent(term.trim())}`);
  };

  return (
    <div data-testid={TID.searchPage} className="container-wide py-10">
      <div className="text-xs uppercase tracking-[0.18em] text-turquoise font-semibold mb-2">Buscador</div>
      <h1 className="font-serif font-black text-4xl sm:text-5xl text-blueblack">Resultados</h1>
      {q && (
        <p data-testid={TID.searchQuery} className="mt-2 text-textgray">
          Búsqueda: <span className="font-semibold text-nightblue">“{q}”</span>
          {!data.loading && ` · ${data.total} resultado${data.total === 1 ? "" : "s"}`}
        </p>
      )}
      <div className="mt-4 h-[3px] w-16 bg-watergreen" />

      <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-2xl">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-textgray" />
          <Input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Buscar noticias, autores, categorías, temas…"
            className="pl-9 h-11 bg-white border-softblue focus-visible:ring-turquoise"
          />
        </div>
        <Button
          type="submit"
          className="h-11 bg-nightblue hover:bg-blueblack text-white rounded-none px-6 font-semibold uppercase text-xs tracking-widest"
        >
          Buscar
        </Button>
      </form>

      <div className="mt-12">
        {data.loading ? (
          <GridSkeleton count={8} columns={4} />
        ) : data.items.length === 0 ? (
          q ? (
            <div className="border border-softblue bg-white p-10 text-center">
              <p className="text-textgray">No se encontraron noticias para tu búsqueda.</p>
              <p className="text-xs text-textgray/80 mt-2">Probá con términos más generales o revisá la ortografía.</p>
            </div>
          ) : (
            <p className="text-textgray text-sm">Escribí un término y presioná buscar.</p>
          )
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {data.items.map((a) => (
              <NewsCard key={a.id} article={a} variant="default" />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
